import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import RecordRTC from 'recordrtc'

type RecorderConfig = {
  disableAutoStop?: boolean
  duration?: number
  onRecordingStopped?: (data: { blob: Blob; isAutoStop: boolean; name: string }) => void
}

export function useVideoRecorder(config: RecorderConfig = {}) {
  const { disableAutoStop = false, duration = 10, onRecordingStopped } = config
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [recorder, setRecorder] = useState<RecordRTC | null>(null)
  const [recordingTime, setRecordingTime] = useState<number>(0)
  const [isRecording, setIsRecording] = useState<boolean>(false)

  const handleRecordingStopped = useCallback((recorder: RecordRTC, isAutoStop: boolean) => {
    const blob = recorder.getBlob()
    const fileName = encodeURIComponent('video-' + new Date().getTime() + '.webm')
    onRecordingStopped?.({ blob, isAutoStop, name: fileName })

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
      recordingTimerRef.current = null
      setRecordingTime(0)
    }
    setRecorder(null)
    setIsRecording(false)
  }, [onRecordingStopped])

  const record = useCallback(
    (stream: MediaStream) => {
      let recorder = new RecordRTC(stream, {
        type: 'video',
        mimeType: 'video/webm',
        recorderType: RecordRTC.MediaStreamRecorder,
        // disable logs
        disableLogs: true,
      })
      if (!disableAutoStop) {
        recorder.setRecordingDuration(duration * 1000, () => {
          handleRecordingStopped(recorder, true)
        })
      }
      recorder.startRecording()
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prevRecordingTime) => prevRecordingTime + 1)
      }, 1000)
      setRecorder(recorder)
    },
    [disableAutoStop, duration, handleRecordingStopped]
  )

  const startRecording = useCallback(
    (stream?: MediaStream) => {
      // It means recording is already started or it is already recording something
      if (recorder) return

      setIsRecording(true)

      if (!stream) {
        navigator.mediaDevices
          .getUserMedia({ video: true })
          .then((mediaStream) => {
            record(mediaStream)
          })
          .catch(console.log)
        return
      }

      record(stream)
    },
    [record, recorder]
  )

  const stopRecording = useCallback(() => {
    if (!recorder) return

    recorder.stopRecording(() => {
      handleRecordingStopped(recorder, false)
    })
  }, [handleRecordingStopped, recorder])

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
        recordingTimerRef.current = null
      }
      if (isRecording && recorder) recorder.stopRecording()
    }
    // eslint-disable-next-line
  }, [])

  const videoRecorder = useMemo(
    () => ({
      isRecording,
      recordingTime,
      startRecording,
      stopRecording,
    }),
    [isRecording, recordingTime, startRecording, stopRecording]
  )

  return videoRecorder
}

export default useVideoRecorder
